import { useEffect, useReducer, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import {
  PROPOSED_DELIVERY_SLOTS,
  cutoffForOccurrence,
  createDeliveryChoice,
  nextEligibleOccurrence,
} from '../../data/delivery'
import { motionTokens } from '../../motion/motionTokens'
import type { BucketSceneController } from '../../three/bucket/BucketSceneController'
import type {
  MysteryBucketPlanAction,
  MysteryBucketService,
} from '../../types'
import { composeBucket } from './bucketComposer'
import type {
  Adventure,
  BucketComposition,
  BucketMode,
  TasteId,
} from './bucket.types'
import {
  bucketReducer,
  createInitialBucketSession,
  type BucketAction,
  type BucketSession,
  type BucketStage as BucketStageId,
} from './bucketReducer'
import { BucketAgent } from './BucketAgent'
import { getLatestCustomerAnswer } from './bucketGuideTranscript'
import { MysteryBucketPlanPreview } from './MysteryBucketPlanPreview'
import {
  SurpriseBucketOffer,
  type MysteryDeliveryOption,
  type MysteryPlanSelection,
} from './SurpriseBucketOffer'
import '../../styles/create-bucket.css'

interface CreateBucketExperienceProps {
  readonly onAddBucket: (
    composition: BucketComposition,
    service?: MysteryBucketService,
  ) => void
  readonly onOpenCart?: () => void
  readonly hasMysteryPlanInCart?: boolean
  readonly planCheckoutComplete?: boolean
  readonly managedPlanService?: MysteryBucketService | null
  readonly onPlanAction?: (action: MysteryBucketPlanAction) => void
  readonly onRemoveMysteryPlan?: () => void
  readonly onSceneControllerChange?: (
    controller: BucketSceneController | null,
  ) => void
  readonly onScoopDrop?: (flavourId: string, seed: number) => void
  readonly onSealReveal?: () => Promise<void>
}

type ExperienceAction =
  | BucketAction
  | { readonly type: 'replace_session'; readonly session: BucketSession }

const tastes: ReadonlyArray<{ id: TasteId; label: string }> = [
  { id: 'fruity', label: 'Fruity' },
  { id: 'freeze-dried', label: 'Freeze-dried' },
  { id: 'chocolate', label: 'Chocolate' },
  { id: 'sour', label: 'Sour' },
  { id: 'caramel', label: 'Caramel' },
]

const agentCopy: Record<
  BucketStageId,
  { eyebrow: string; title: string; body: string }
> = {
  eligibility: {
    eyebrow: 'A quick delivery check',
    title: 'First up—where are we delivering?',
    body: 'Share your postcode and we’ll show the local delivery runs that could serve it.',
  },
  recipient: {
    eyebrow: 'Let’s make it personal',
    title: 'Who are we building this bucket for?',
    body: 'Choose who will be opening it, and we’ll keep the next questions relevant.',
  },
  preferences: {
    eyebrow: 'Your favourites',
    title: 'What do you always reach for?',
    body: 'Choose the flavours you love and anything we should never put in.',
  },
  adventure: {
    eyebrow: 'One last flavour question',
    title: 'How adventurous should we make it?',
    body: 'Keep things familiar, add one wildcard, or ask the Sub Zero team to surprise you.',
  },
  budget: {
    eyebrow: 'Choose the bucket size',
    title: 'Which bucket size suits you?',
    body: 'Pick Little, Classic or Big. The service review will use the actual mix price.',
  },
  revealed: {
    eyebrow: 'Your Mystery Bucket',
    title: 'How often should we drop the surprise?',
    body: 'Choose one-off, weekly or fortnightly, then pick the local delivery run that suits you.',
  },
  surprise: {
    eyebrow: 'Service review',
    title: 'Check the operating plan before it reaches the cart',
    body: 'The bucket, cadence, delivery run, cut-off and per-drop total are all visible here.',
  },
  demo_complete: {
    eyebrow: 'Plan controls',
    title: 'This is how the customer manages the next drop',
    body: 'Review the cart, skip a bucket, pause deliveries or cancel the plan—all as a clickable preview.',
  },
}

function experienceReducer(
  state: BucketSession,
  action: ExperienceAction,
): BucketSession {
  if (action.type === 'replace_session') return action.session
  return bucketReducer(state, action)
}

function replaySession(
  source: BucketSession,
  target: 'preferences' | 'adventure',
): BucketSession {
  let next = createInitialBucketSession()
  next = bucketReducer(next, {
    type: 'postcode_submitted',
    postcode: source.eligibility.postcode,
  })
  if (!source.mode) return next

  next = bucketReducer(next, { type: 'mode_selected', mode: source.mode })
  next = bucketReducer(next, {
    type: 'likes_updated',
    likes: source.preferences.likes,
  })
  next = bucketReducer(next, {
    type: 'hard_nos_updated',
    hardNos: source.preferences.hardNos,
  })
  if (target === 'preferences') return next

  return bucketReducer(next, { type: 'preferences_confirmed' })
}

function formatMoney(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(cents / 100)
}

function formatProposedDrop(date: Date): string {
  return new Intl.DateTimeFormat('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'Australia/Brisbane',
  }).format(date)
}

function formatChangeCutoff(date: Date): string {
  return `${new Intl.DateTimeFormat('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Australia/Brisbane',
  }).format(date)}, 12:00 pm AEST`
}

export function CreateBucketExperience({
  onAddBucket,
  onOpenCart,
  hasMysteryPlanInCart,
  planCheckoutComplete = false,
  managedPlanService,
  onPlanAction,
  onRemoveMysteryPlan,
  onSceneControllerChange,
  onSealReveal,
}: CreateBucketExperienceProps) {
  const [session, dispatch] = useReducer(
    experienceReducer,
    undefined,
    createInitialBucketSession,
  )
  const [postcode, setPostcode] = useState('')
  const [pendingComposition, setPendingComposition] =
    useState<BucketComposition | null>(null)
  const stageHeadingRef = useRef<HTMLHeadingElement>(null)
  const previousStageRef = useRef(session.stage)
  const reduceMotion = useReducedMotion()
  const [planDraft, setPlanDraft] = useState<MysteryBucketService | null>(
    null,
  )
  const [planPending, setPlanPending] = useState(false)
  const [staffEditorialAvailable, setStaffEditorialAvailable] = useState(true)
  const deliveryOptions: readonly MysteryDeliveryOption[] =
    session.eligibility.status === 'eligible'
      ? PROPOSED_DELIVERY_SLOTS.map((slot) => ({
          id: slot.id,
          label: slot.label,
          firstDropLabel: formatProposedDrop(
            nextEligibleOccurrence(slot, new Date()),
          ),
        }))
      : []

  useEffect(() => {
    onSceneControllerChange?.(null)
  }, [onSceneControllerChange])

  const copy = agentCopy[session.stage]
  const latestCustomerAnswer = getLatestCustomerAnswer(session)
  const stagedScoopCount =
    pendingComposition?.lines.length ?? session.composition?.lines.length ?? 0
  const recipientLabel =
    session.mode === 'self'
      ? 'Just me'
      : session.mode === 'family'
        ? 'The family'
        : session.mode === 'gift'
          ? 'A gift'
          : 'Not chosen'
  const cadenceLabel =
    session.cadence === 'once'
      ? 'One-off'
      : session.cadence === 'weekly'
        ? 'Weekly'
        : session.cadence === 'fortnightly'
          ? 'Fortnightly'
          : 'Not chosen'
  const optionHover = reduceMotion
    ? undefined
    : { y: -3, scale: 1.02 }
  const optionTap = reduceMotion ? undefined : { scale: 0.96 }
  const optionTransition = reduceMotion
    ? { duration: motionTokens.reducedOpacity }
    : motionTokens.spring

  useEffect(() => {
    if (previousStageRef.current === session.stage) return
    previousStageRef.current = session.stage
    stageHeadingRef.current?.focus({ preventScroll: true })
  }, [session.stage])

  useEffect(() => {
    if (!managedPlanService || session.stage !== 'demo_complete') return
    setPlanDraft(managedPlanService)
  }, [managedPlanService, session.stage])

  useEffect(() => {
    if (
      managedPlanService ||
      hasMysteryPlanInCart !== false ||
      session.stage !== 'demo_complete' ||
      !planDraft
    ) {
      return
    }

    setPlanDraft(null)
    setPlanPending(false)
    dispatch({ type: 'plan_edit_requested' })
  }, [hasMysteryPlanInCart, managedPlanService, planDraft, session.stage])

  function resetExperience() {
    if (hasMysteryPlanInCart || managedPlanService) {
      onRemoveMysteryPlan?.()
    }
    dispatch({ type: 'reset' })
    setPostcode('')
    setPendingComposition(null)
    setPlanDraft(null)
    setPlanPending(false)
  }

  function selectMode(mode: BucketMode) {
    dispatch({ type: 'mode_selected', mode })
  }

  function toggleLike(taste: TasteId) {
    const likes = session.preferences.likes.includes(taste)
      ? session.preferences.likes.filter((item) => item !== taste)
      : [...session.preferences.likes, taste]
    const hardNos = session.preferences.hardNos.filter(
      (item) => item !== taste,
    )
    dispatch({ type: 'hard_nos_updated', hardNos })
    dispatch({ type: 'likes_updated', likes })
  }

  function toggleHardNo(taste: TasteId) {
    const hardNos = session.preferences.hardNos.includes(taste)
      ? session.preferences.hardNos.filter((item) => item !== taste)
      : [...session.preferences.hardNos, taste]
    const likes = session.preferences.likes.filter(
      (item) => item !== taste,
    )
    dispatch({ type: 'likes_updated', likes })
    dispatch({ type: 'hard_nos_updated', hardNos })
  }

  function selectAdventure(adventure: Adventure) {
    dispatch({ type: 'adventure_selected', adventure })
    setPendingComposition(null)
  }

  function selectBudget(targetSpendCents: number) {
    dispatch({ type: 'budget_selected', targetSpendCents })
    if (!session.mode) return

    setPendingComposition(
      composeBucket({
        mode: session.mode,
        likes: session.preferences.likes,
        hardNos: session.preferences.hardNos,
        adventure: session.preferences.adventure,
        targetSpendCents,
      }),
    )
    setPlanDraft(null)
  }

  function createService(
    selection: MysteryPlanSelection,
  ): MysteryBucketService | null {
    if (session.eligibility.status !== 'eligible') return null

    const slot = PROPOSED_DELIVERY_SLOTS.find(
      (candidate) => candidate.id === selection.deliverySlotId,
    )
    if (!slot) return null

    const now = new Date()
    const firstDrop = nextEligibleOccurrence(slot, now)
    const cutoff = cutoffForOccurrence(slot, firstDrop)
    return {
      cadence: selection.cadence,
      lifecycleStatus: 'in_cart',
      delivery: createDeliveryChoice(slot, now),
      firstDropIso: firstDrop.toISOString(),
      deliveryFeeCents: 500,
      bucketSubtotalCents:
        session.composition?.subtotalCents ??
        session.preferences.targetSpendCents,
      postcode: session.eligibility.postcode,
      zoneStatus:
        session.eligibility.status === 'eligible'
          ? 'eligible'
          : 'unconfirmed',
      cutoffIso: cutoff.toISOString(),
      cutoffLabel: formatChangeCutoff(cutoff),
    }
  }

  function confirmPlanSelection(selection: MysteryPlanSelection) {
    if (!session.composition || planPending) return
    const service = createService(selection)
    if (!service) return

    setPlanDraft(service)
    dispatch({ type: 'cadence_selected', cadence: selection.cadence })

    if (selection.cadence === 'once') {
      onAddBucket(session.composition, service)
      return
    }

    dispatch({
      type: 'delivery_selected',
      deliverySlotId: selection.deliverySlotId,
    })
  }

  async function addRecurringPlan() {
    if (
      planPending ||
      !session.composition ||
      !planDraft ||
      planDraft.cadence === 'once'
    ) {
      return
    }

    setPlanPending(true)
    try {
      await onSealReveal?.()
      onAddBucket(session.composition, planDraft)
      dispatch({ type: 'plan_added' })
    } finally {
      setPlanPending(false)
    }
  }

  function changePlan(removeFromCart = false) {
    if (removeFromCart) onRemoveMysteryPlan?.()
    setPlanDraft(null)
    setPlanPending(false)
    dispatch({ type: 'plan_edit_requested' })
  }

  function renderControls() {
    switch (session.stage) {
      case 'eligibility':
        return (
          <form
            className="bucket-form"
            onSubmit={(event) => {
              event.preventDefault()
              dispatch({ type: 'postcode_submitted', postcode })
            }}
          >
            <label htmlFor="bucket-postcode">Delivery postcode</label>
            <div className="bucket-form__row">
              <input
                id="bucket-postcode"
                inputMode="numeric"
                maxLength={4}
                value={postcode}
                onChange={(event) =>
                  setPostcode(event.currentTarget.value.replace(/\D/g, ''))
                }
                placeholder="e.g. 4680"
                autoComplete="postal-code"
              />
              <button className="bucket-button bucket-button--primary" type="submit">
                Check my delivery
              </button>
            </div>
            {session.eligibility.status === 'invalid' && (
              <p className="bucket-notice bucket-notice--error" role="alert">
                That needs four digits. Try 4680 or 4700 for this demo.
              </p>
            )}
            <button
              className="bucket-text-button"
              type="button"
              onClick={resetExperience}
            >
              Clear my demo profile
            </button>
          </form>
        )

      case 'recipient':
        return (
          <fieldset className="bucket-options">
            <legend className="sr-only">Choose who the bucket is for</legend>
            <motion.button
              type="button"
              whileHover={optionHover}
              whileTap={optionTap}
              transition={optionTransition}
              onClick={() => selectMode('self')}
            >
              <span>Just me</span>
              <small>My private stash</small>
            </motion.button>
            <motion.button
              type="button"
              whileHover={optionHover}
              whileTap={optionTap}
              transition={optionTransition}
              onClick={() => selectMode('family')}
            >
              <span>The family</span>
              <small>A shareable crowd-pleaser</small>
            </motion.button>
            <motion.button
              type="button"
              whileHover={optionHover}
              whileTap={optionTap}
              transition={optionTransition}
              onClick={() => selectMode('gift')}
            >
              <span>A lucky human</span>
              <small>Build a gift bucket</small>
            </motion.button>
          </fieldset>
        )

      case 'preferences':
        return (
          <>
            <fieldset className="bucket-chipset">
              <legend>Pick the flavours you love</legend>
              <div>
                {tastes.map((taste) => (
                  <button
                    type="button"
                    key={taste.id}
                    aria-pressed={session.preferences.likes.includes(taste.id)}
                    onClick={() => toggleLike(taste.id)}
                  >
                    {taste.label}
                  </button>
                ))}
              </div>
            </fieldset>
            <fieldset className="bucket-hard-nos">
              <legend>Anything we should never put in?</legend>
              <div>
                {tastes.map((taste) => (
                  <button
                    type="button"
                    key={taste.id}
                    aria-pressed={session.preferences.hardNos.includes(taste.id)}
                    onClick={() => toggleHardNo(taste.id)}
                  >
                    No {taste.label.toLowerCase()}
                  </button>
                ))}
              </div>
            </fieldset>
            <button
              className="bucket-allergen-button"
              type="button"
              onClick={() =>
                dispatch({ type: 'allergen_concern_recorded' })
              }
            >
              I have an allergen concern
            </button>
            {session.status === 'allergen_review' ? (
              <div className="bucket-notice bucket-notice--stop" role="alert">
                <strong>Let’s stop the automated recommendation here.</strong>
                <span>
                  This demo cannot confirm allergen suitability. A real service
                  would require a human-reviewed product and handling check.
                </span>
              </div>
            ) : (
              session.preferences.likes.length > 0 && (
                <button
                  className="bucket-button bucket-button--primary"
                  type="button"
                  onClick={() =>
                    dispatch({ type: 'preferences_confirmed' })
                  }
                >
                  Continue
                </button>
              )
            )}
          </>
        )

      case 'adventure':
        return (
          <fieldset className="bucket-options">
            <legend className="sr-only">Choose an adventure level</legend>
            <motion.button
              type="button"
              whileHover={optionHover}
              whileTap={optionTap}
              transition={optionTransition}
              onClick={() => selectAdventure('safe')}
            >
              <span>Keep it familiar</span>
              <small>Only flavours I picked</small>
            </motion.button>
            <motion.button
              type="button"
              whileHover={optionHover}
              whileTap={optionTap}
              transition={optionTransition}
              onClick={() => selectAdventure('one_wildcard')}
            >
              <span>One wildcard</span>
              <small>A little theatrical twist</small>
            </motion.button>
            <motion.button
              type="button"
              whileHover={optionHover}
              whileTap={optionTap}
              transition={optionTransition}
              onClick={() => selectAdventure('wild')}
            >
              <span>Full improv</span>
              <small>Surprise me</small>
            </motion.button>
          </fieldset>
        )

      case 'budget':
        return (
          <>
            <fieldset className="bucket-options bucket-options--budget">
              <legend className="sr-only">Choose a target budget</legend>
              <motion.button
                type="button"
                whileHover={optionHover}
                whileTap={optionTap}
                transition={optionTransition}
                onClick={() => selectBudget(2500)}
              >
                {pendingComposition &&
                  session.preferences.targetSpendCents === 2500 && (
                    <motion.span
                      className="bucket-selection-halo"
                      data-testid="bucket-selection-halo"
                      layoutId="bucket-selection"
                      transition={optionTransition}
                    />
                  )}
                <span>Little Bucket · $25</span>
                <small>A tight little edit</small>
              </motion.button>
              <motion.button
                type="button"
                whileHover={optionHover}
                whileTap={optionTap}
                transition={optionTransition}
                onClick={() => selectBudget(3500)}
              >
                {pendingComposition &&
                  session.preferences.targetSpendCents === 3500 && (
                    <motion.span
                      className="bucket-selection-halo"
                      data-testid="bucket-selection-halo"
                      layoutId="bucket-selection"
                      transition={optionTransition}
                    />
                  )}
                <span>Classic Bucket · $35</span>
                <small>Most popular · the sweet spot</small>
              </motion.button>
              <motion.button
                type="button"
                whileHover={optionHover}
                whileTap={optionTap}
                transition={optionTransition}
                onClick={() => selectBudget(5000)}
              >
                {pendingComposition &&
                  session.preferences.targetSpendCents === 5000 && (
                    <motion.span
                      className="bucket-selection-halo"
                      data-testid="bucket-selection-halo"
                      layoutId="bucket-selection"
                      transition={optionTransition}
                    />
                  )}
                <span>Big Bucket · $50</span>
                <small>Built for sharing</small>
              </motion.button>
            </fieldset>
            {pendingComposition?.status === 'ready' && (
              <motion.button
                className="bucket-button bucket-button--reveal"
                type="button"
                initial={
                  reduceMotion ? false : { opacity: 0, scale: 0.92 }
                }
                animate={{ opacity: 1, scale: 1 }}
                transition={
                  reduceMotion ? { duration: 0 } : { duration: 0.2 }
                }
                onClick={() =>
                  dispatch({
                    type: 'composition_revealed',
                    composition: pendingComposition,
                  })
                }
              >
                Show my Mystery Bucket
              </motion.button>
            )}
            {pendingComposition?.status === 'no_match' && (
              <div className="bucket-notice bucket-notice--stop" role="status">
                <strong>We couldn’t build an honest match.</strong>
                <span>
                  Your likes and hard no’s leave no eligible demo treats.
                  Change a taste instead of revealing an empty bucket.
                </span>
                <button
                  className="bucket-text-button"
                  type="button"
                  onClick={() => {
                    dispatch({
                      type: 'replace_session',
                      session: replaySession(session, 'preferences'),
                    })
                    setPendingComposition(null)
                  }}
                >
                  Edit tastes
                </button>
              </div>
            )}
            <button
              className="bucket-text-button"
              type="button"
              onClick={() => {
                dispatch({
                  type: 'replace_session',
                  session: replaySession(session, 'adventure'),
                })
                setPendingComposition(null)
              }}
            >
              Back
            </button>
          </>
        )

      case 'revealed':
        if (!session.composition) return null
        return (
          <>
            <article className="bucket-reveal">
              <p className="bucket-reveal__kicker">
                Likely first mix · stock permitting
              </p>
              <h3>{session.composition.name}</h3>
              <p className="bucket-reveal__price">
                {formatMoney(session.composition.subtotalCents)}
              </p>
              <ul>
                {session.composition.lines.map((line) => (
                  <li key={line.productId}>
                    <img src={line.image} alt="" />
                    <span>
                      <strong>{line.name}</strong>
                      <small>{line.reason}</small>
                    </span>
                    <b>{formatMoney(line.priceCents)}</b>
                  </li>
                ))}
              </ul>
            </article>
            <SurpriseBucketOffer
              compositionName={session.composition.name}
              bucketPriceCents={session.composition.subtotalCents}
              deliveryFeeCents={500}
              deliveryOptions={deliveryOptions}
              subscriptionDisabled={session.mode === 'gift'}
              onConfirm={confirmPlanSelection}
              onEditBucket={() => {
                dispatch({
                  type: 'replace_session',
                  session: replaySession(session, 'preferences'),
                })
                setPendingComposition(null)
                setPlanDraft(null)
              }}
            />
          </>
        )

      case 'surprise':
        if (!session.composition || !planDraft) return null
        return (
          <MysteryBucketPlanPreview
            service={planDraft}
            compositionName={session.composition.name}
            confirmed={false}
            inCart={false}
            pending={planPending}
            onAddPlan={() => {
              void addRecurringPlan()
            }}
            onReviewCart={() => onOpenCart?.()}
            onChangePlan={() => changePlan()}
            onStartOver={resetExperience}
          />
        )

      case 'demo_complete':
        {
          const currentPlan = managedPlanService ?? planDraft
          if (!session.composition || !currentPlan) break

          if (currentPlan.cadence !== 'once') {
            return (
              <MysteryBucketPlanPreview
                service={currentPlan}
                compositionName={session.composition.name}
                confirmed={
                  planCheckoutComplete ||
                  currentPlan.lifecycleStatus !== 'in_cart'
                }
                inCart
                pending={false}
                onAddPlan={() => undefined}
                onReviewCart={() => onOpenCart?.()}
                onChangePlan={() => changePlan(true)}
                onPlanAction={(action) => onPlanAction?.(action)}
                onStartOver={resetExperience}
              />
            )
          }

          return (
            <div className="bucket-encore bucket-encore--operational">
              <span aria-hidden="true">✓</span>
              <p className="bucket-reveal__kicker">One-off order preview</p>
              <h3>Your Mystery Bucket is lined up.</h3>
              <dl>
                <div>
                  <dt>First drop</dt>
                  <dd>{formatProposedDrop(new Date(currentPlan.firstDropIso))}</dd>
                </div>
                <div>
                  <dt>Delivery</dt>
                  <dd>{currentPlan.delivery.window}</dd>
                </div>
                <div>
                  <dt>Total</dt>
                  <dd>
                    {formatMoney(
                      currentPlan.bucketSubtotalCents +
                        currentPlan.deliveryFeeCents,
                    )}
                  </dd>
                </div>
              </dl>
              <p>
                The bucket and its selected local run are in the demo cart.
                Nothing has been ordered or charged.
              </p>
              <p>
                Starting a replacement removes this one-off bucket from the demo
                cart before the next profile begins.
              </p>
              <div className="bucket-encore__actions">
                <button
                  className="bucket-button bucket-button--primary"
                  type="button"
                  onClick={() => onOpenCart?.()}
                >
                  Review the delivery cart
                </button>
                <button
                  className="bucket-text-button"
                  type="button"
                  onClick={resetExperience}
                >
                  Replace this bucket
                </button>
              </div>
            </div>
          )
        }

        return (
          <div className="bucket-encore">
            <span aria-hidden="true">✓</span>
            <h3>One-off bucket selected</h3>
            <p>
              Added to the local demo cart. No order, payment or delivery
              booking was created.
            </p>
            <button
              className="bucket-button bucket-button--primary"
              type="button"
              onClick={resetExperience}
            >
              Build another demo
            </button>
          </div>
        )
    }
  }

  return (
    <section
      id="create-a-bucket"
      className="create-bucket"
      data-shop-section="create-a-bucket"
      aria-labelledby="create-bucket-title"
    >
      <div className="create-bucket__marquee" aria-hidden="true">
        <span>CHOOSE THE MIX</span>
        <i>✦</i>
        <span>PICK A CADENCE</span>
        <i>✦</i>
        <span>SET THE DROP</span>
        <i>✦</i>
        <span>SKIP ANYTIME</span>
      </div>
      <div className="create-bucket__intro">
        <p className="create-bucket__overline">
          Your Sub Zero Mystery Bucket · operating preview
        </p>
        <h1 id="create-bucket-title">
          Pick the guardrails.
          <br />
          <em>We’ll pack the surprise.</em>
        </h1>
        <p className="create-bucket__intro-copy">
          Build the taste profile, choose weekly or fortnightly, select the
          local run and see the complete per-delivery price.
        </p>
      </div>
      <div className="create-bucket__shell">
        <div className="create-bucket__theatre">
          <div className="campaign-bucket-stage" aria-hidden="true">
            <picture className="campaign-bucket-stage__picture">
              <img
                className="campaign-bucket-stage__image"
                src={
                  staffEditorialAvailable
                    ? '/media/higgsfield/editorial/shop-staff-guide.webp'
                    : '/media/higgsfield/sweet-impact-mobile.avif?v=20260801-baked'
                }
                alt=""
                loading="lazy"
                onError={() => setStaffEditorialAvailable(false)}
              />
            </picture>
            {!staffEditorialAvailable && (
              <img
                className="campaign-bucket-stage__logo"
                src="/brand/sub-zero-logo-primary.png"
                alt=""
                loading="lazy"
              />
            )}
            <div className="campaign-bucket-stage__status">
              <span>Bucket in progress</span>
              <strong>{recipientLabel}</strong>
              <small>
                {stagedScoopCount > 0
                  ? `${stagedScoopCount} likely treats · ${formatMoney(
                      session.preferences.targetSpendCents,
                    )} target`
                  : 'Your guide is waiting for the first answer'}
              </small>
            </div>
          </div>
          <AnimatePresence mode="popLayout" initial={false}>
            {stagedScoopCount > 0 && (
              <motion.p
                className="bucket-scoop-counter"
                key={stagedScoopCount}
                initial={
                  reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.9 }
                }
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={
                  reduceMotion ? { opacity: 0 } : { opacity: 0, y: -6, scale: 0.94 }
                }
                transition={optionTransition}
                aria-live="polite"
              >
                {stagedScoopCount}{' '}
                {stagedScoopCount === 1 ? 'scoop staged' : 'scoops staged'}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        <div className="create-bucket__console">
          <BucketAgent
            stage={session.stage}
            eyebrow={copy.eyebrow}
            title={copy.title}
            body={copy.body}
            customerAnswer={latestCustomerAnswer}
            headingRef={stageHeadingRef}
          />
          <div className="create-bucket__controls">
            {session.stage !== 'eligibility' &&
              session.eligibility.status === 'unconfirmed' && (
                <div className="bucket-notice" role="status">
                  <strong>
                    That postcode isn’t confirmed for the proposed pilot.
                  </strong>
                  <span>
                    You can explore the bucket builder, but this demo won’t
                    promise a delivery.
                  </span>
                </div>
              )}
            {renderControls()}
          </div>
          <aside className="create-bucket__promise" aria-label="Your bucket so far">
            <span>Your bucket so far</span>
            <dl>
              <div>
                <dt>Postcode</dt>
                <dd>{session.eligibility.postcode || 'Not checked'}</dd>
              </div>
              <div>
                <dt>For</dt>
                <dd>{recipientLabel}</dd>
              </div>
              <div>
                <dt>Favourites</dt>
                <dd>
                  {session.preferences.likes.length > 0
                    ? `${session.preferences.likes.length} selected`
                    : 'Not chosen'}
                </dd>
              </div>
              <div>
                <dt>Bucket</dt>
                <dd>{formatMoney(session.preferences.targetSpendCents)}</dd>
              </div>
              <div>
                <dt>Plan</dt>
                <dd>{cadenceLabel}</dd>
              </div>
            </dl>
          </aside>
          <p className="create-bucket__trust">
            Interactive demo — no order, payment, delivery booking, email, SMS
            or subscription is created.
          </p>
          {session.stage !== 'eligibility' && (
            <button
              className="bucket-text-button bucket-text-button--reset"
              type="button"
              onClick={resetExperience}
            >
              Start over
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
